/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card as CardInfo,
  CardContent,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Api from '../../../api';
import { InvoiceByIdResponseDTO } from '../../../api-client/generated/api';
import { getCurrency, statusStyle } from '../../../utils/general';
import {
  accentColors,
  secondaryColors,
  warningColors
} from '../../../theme/themeConfig';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import { Version } from '../../../utils/types/ProjectType';
import VersionDropdown from '../VersionDropdown';
import InvoiceDrawer, { StatusPill } from './InvoiceDrawer';

interface Props {
  detailProject: any;
  isClient?: boolean;
  onLoadVersion: (projectId: string) => void;
  versions: Version[];
}

const InvoicesList: React.FC<Props> = function InvoicesList({
  detailProject,
  isClient = false,
  versions,
  onLoadVersion
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'PAID'>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [typeModal, setTypeModal] = useState('');
  const [sentFlag, setSentFlag] = useState(false);
  const [showOkModal, setShowOkModal] = useState(false);

  const allDataToSend = isClient
    ? { id: detailProject?.id, url: 'client-view/get-invoices-by-project' }
    : [detailProject.id, ''];
  const getInvoiceAction = isClient
    ? Api.ClientApi.getProjectInvoices
    : Api.InvoicesApi.getInvoicesList;
  const { data: allInvoices, mutate: mutateAll } = useSWR(
    detailProject?.id !== undefined ? allDataToSend : null,
    getInvoiceAction
  );
  const invoices: any[] = allInvoices ?? [];
  const filtered =
    filter === 'ALL'
      ? invoices
      : invoices.filter((inv) => inv.status?.toUpperCase() === filter);

  const toCurrency = (dollars: number | string) =>
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      style: 'currency'
    }).format(parseFloat((parseFloat(String(dollars ?? 0)) / 100).toFixed(2)));

  const totalInvoiced = invoices.reduce(
    (s, inv) => s + parseFloat(String(inv.amount ?? 0)),
    0
  );
  const totalPaid = invoices
    .filter((inv) => inv.status?.toUpperCase() === 'PAID')
    .reduce((s, inv) => s + parseFloat(String(inv.amount ?? 0)), 0);
  const totalOutstanding = invoices
    .filter((inv) => inv.status?.toUpperCase() === 'PENDING')
    .reduce((s, inv) => s + parseFloat(String(inv.amount ?? 0)), 0);

  const { data: updateStatusData, error: updateStatusError } = useSWR(
    typeModal === 'status' && sentFlag ? [selectedId, 'Paid'] : null,
    Api.InvoicesApi.updateInvoiceByStatus
  );
  useEffect(() => {
    if (updateStatusData && !updateStatusError) {
      setSentFlag(false);
      setSelectedId(null);
      setDrawerOpen(false);
      mutateAll();
    }
  }, [updateStatusData]);

  const { data: resendData, error: resendError } = useSWR(
    typeModal === 'resendInvoice' && sentFlag ? selectedId : null,
    Api.InvoicesApi.resendInvoiceById
  );
  useEffect(() => {
    if (resendData && !resendError) setSentFlag(false);
  }, [resendData]);
  useEffect(() => {
    if (sentFlag) setIsModalOpen(false);
  }, [sentFlag]);

  const openDrawer = (id: string) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography
          sx={{ color: '#0f1c2e', fontSize: '18px', fontWeight: 700 }}
        >
          Invoices
        </Typography>
        <Box sx={{ minWidth: 200 }}>
          <VersionDropdown
            currentProjectId={detailProject?.id}
            onChange={onLoadVersion}
            versions={versions}
          />
        </Box>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box
          sx={{
            backgroundColor: '#F5F6F8',
            borderRadius: '10px',
            flex: 1,
            px: 2,
            py: '14px'
          }}
        >
          <Typography
            sx={{ color: '#0f1c2e', fontSize: '22px', fontWeight: 700 }}
          >
            {toCurrency(totalInvoiced)}
          </Typography>
          <Typography sx={{ color: '#7D8090', fontSize: '12px', mt: '2px' }}>
            Total Invoiced
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: '#E8F5E9',
            borderRadius: '10px',
            flex: 1,
            px: 2,
            py: '14px'
          }}
        >
          <Typography
            sx={{ color: '#2E7D32', fontSize: '22px', fontWeight: 700 }}
          >
            {toCurrency(totalPaid)}
          </Typography>
          <Typography sx={{ color: '#4CAF50', fontSize: '12px', mt: '2px' }}>
            Paid
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: '#FFF8E1',
            borderRadius: '10px',
            flex: 1,
            px: 2,
            py: '14px'
          }}
        >
          <Typography
            sx={{ color: '#E65100', fontSize: '22px', fontWeight: 700 }}
          >
            {toCurrency(totalOutstanding)}
          </Typography>
          <Typography sx={{ color: '#FF9800', fontSize: '12px', mt: '2px' }}>
            Outstanding
          </Typography>
        </Box>
      </Box>

      {/* Invoice table */}
      <Box
        sx={{
          border: '1px solid #E2E4EA',
          borderRadius: '10px',
          overflow: 'hidden'
        }}
      >
        {/* Header + filter */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            px: 2,
            py: '12px'
          }}
        >
          <Typography
            sx={{ color: '#0f1c2e', fontSize: '14px', fontWeight: 600 }}
          >
            All Invoices
          </Typography>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            {(['ALL', 'PENDING', 'PAID'] as const).map((f) => (
              <Box
                key={f}
                onClick={() => setFilter(f)}
                sx={{
                  backgroundColor: filter === f ? '#005DA2' : 'transparent',
                  border:
                    filter === f ? '1px solid #005DA2' : '1px solid #E2E4EA',
                  borderRadius: '20px',
                  color: filter === f ? '#fff' : '#7D8090',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  px: '12px',
                  py: '4px',
                  transition: 'all 0.15s',
                  userSelect: 'none'
                }}
              >
                {f === 'ALL' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
              </Box>
            ))}
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F5F6F8' }}>
                {[
                  '# INVOICE',
                  'DESCRIPTION',
                  'AMOUNT',
                  'DATE',
                  'STATUS',
                  ''
                ].map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      borderColor: '#E2E4EA',
                      borderTop: '1px solid #E2E4EA',
                      color: '#7D8090',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      py: '10px'
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      border: 0,
                      color: '#7D8090',
                      fontSize: '13px',
                      py: 4,
                      textAlign: 'center'
                    }}
                  >
                    No invoices found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((inv: any) => (
                <TableRow
                  key={inv.id}
                  onClick={() => openDrawer(inv.id)}
                  sx={{
                    '&:hover': { backgroundColor: '#F9FAFC' },
                    '&:last-child td': { borderBottom: 0 },
                    cursor: 'pointer'
                  }}
                >
                  <TableCell
                    sx={{
                      borderColor: '#F0F1F3',
                      fontSize: '12px',
                      fontWeight: 700,
                      py: '12px'
                    }}
                  >
                    INV-{String(inv.invoiceNumber).padStart(5, '0')}
                    {(inv as any).serviceRequestIds?.length > 0 && (
                      <Chip
                        label="⚡ SR"
                        size="small"
                        sx={{
                          background: '#EDE9FE',
                          color: '#7C3AED',
                          fontSize: '10px',
                          fontWeight: 700,
                          ml: 1
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderColor: '#F0F1F3',
                      color: '#4B4D56',
                      fontSize: '12px',
                      py: '12px'
                    }}
                  >
                    {inv.rangeStartDate
                      ? `${inv.projectName ?? 'Service'} — ${dayjs(
                          inv.rangeStartDate
                        ).format('MMM D')}–${dayjs(inv.rangeEndDate).format(
                          'D, YYYY'
                        )}`
                      : inv.projectName ?? '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderColor: '#F0F1F3',
                      fontSize: '12px',
                      fontWeight: 600,
                      py: '12px'
                    }}
                  >
                    {toCurrency(inv.amount)}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderColor: '#F0F1F3',
                      color: '#4B4D56',
                      fontSize: '12px',
                      py: '12px'
                    }}
                  >
                    {inv.sendDate
                      ? dayjs(inv.sendDate).format('MMM D, YYYY')
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ borderColor: '#F0F1F3', py: '12px' }}>
                    <StatusPill status={inv.status} />
                  </TableCell>
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                      openDrawer(inv.id);
                    }}
                    sx={{
                      borderColor: '#F0F1F3',
                      py: '12px',
                      textAlign: 'center',
                      width: 40
                    }}
                  >
                    <VisibilityOutlinedIcon
                      sx={{ color: '#7D8090', fontSize: 16 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <InvoiceDrawer
        invoiceId={drawerOpen ? selectedId : null}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedId(null);
        }}
        onMarkPaid={() => {
          setTypeModal('status');
          setIsModalOpen(true);
        }}
        onResend={() => {
          setTypeModal('resendInvoice');
          setIsModalOpen(true);
        }}
        open={drawerOpen}
      />

      {isModalOpen && typeModal === 'status' && (
        <ConfirmModal
          modalText={t('MODAL.confirModalStatusInvoice')}
          modalType="status"
          setSentFlag={setSentFlag}
          setShowModal={setIsModalOpen}
          setShowOkModal={setShowOkModal}
        />
      )}
      {isModalOpen && typeModal === 'resendInvoice' && (
        <ConfirmModal
          modalText={t('MODAL.confirModalResendInvoice')}
          modalType="resendInvoice"
          setSentFlag={setSentFlag}
          setShowModal={setIsModalOpen}
          setShowOkModal={setShowOkModal}
        />
      )}
    </Box>
  );
};

export default InvoicesList;
